import { MetaLimitNotice } from './CommerceActivity';
import React, { type ReactNode, useEffect, useState } from 'react';

import ChatSidebar from './ChatSidebar';
import Sidebar from './Sidebar';
import OnboardingTourModal from './onboarding/OnboardingTourModal';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fastapi } from '../services/api';
import { initializeChatRuntime, openChatUI } from '../store/chat/chatSlice';
import { getDemoStatus, refreshDemoStatus, setDemoStatus } from '../services/demoModeService';

interface LayoutProps {
  children: ReactNode;
}

const LayoutContent: React.FC<LayoutProps> = ({ children }) => {
  const isCollapsed = useAppSelector((s) => s.ui.isCollapsed);
  const isChatOpen = useAppSelector((s) => s.chat.isOpen);
  const isChatDocked = useAppSelector((s) => s.chat.isDocked);
  const theme = useAppSelector((s) => s.ui.theme);
  const user = useAppSelector((s) => s.auth.user);
  const dispatch = useAppDispatch();
  const [demoChecked, setDemoChecked] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [isTourModalOpen, setIsTourModalOpen] = useState(false);

  useEffect(() => {
    const update = () => setIsDesktop(window.innerWidth >= 1024); // lg breakpoint
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
    document.body.dataset.theme = theme;
  }, [theme]);

  useEffect(() => {
    if (!user) return;
    let disposed = false;
    let timer: number | undefined;
    let checking = false;
    const controller = new AbortController();
    let previousActive = getDemoStatus()?.active ?? false;
    setDemoChecked(false);
    const check = async (initial = false) => {
      if (disposed || checking) return;
      checking = true;
      if (timer) window.clearTimeout(timer);
      try {
        const status = await refreshDemoStatus(controller.signal);
        if (disposed) return;
        if (!initial && previousActive !== status.active) window.location.reload();
        previousActive = status.active;
        if (status.active) {
          const remaining = status.expires_at ? Date.parse(status.expires_at) - Date.now() + 50 : 10_000;
          timer = window.setTimeout(() => void check(), Math.max(1000, Math.min(10_000, remaining || 10_000)));
        }
      } catch {
        if (disposed) return;
        // A transient failure must never route existing demo actions to live APIs.
        if (!getDemoStatus()) setDemoStatus({ allowed: false, active: false, expires_at: null, duration_seconds: 600 });
        timer = window.setTimeout(() => void check(), 30_000);
      } finally {
        checking = false;
        if (!disposed) {
          setDemoChecked(true);
          if (initial) void dispatch(initializeChatRuntime());
        }
      }
    };
    const onVisible = () => { if (!document.hidden) void check(); };
    void check(true);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      disposed = true;
      controller.abort();
      if (timer) window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user?.comercioKey, user?.token, dispatch]);

  useEffect(() => {
    if (!demoChecked || sessionStorage.getItem('taratic:demo:open-chat') !== '1') return;
    sessionStorage.removeItem('taratic:demo:open-chat');
    dispatch(openChatUI());
  }, [demoChecked, dispatch]);

  const hasCheckedTourRef = React.useRef(false);

  // Check onboarding tour status on login / first load
  useEffect(() => {
    if (!user || hasCheckedTourRef.current) return;
    hasCheckedTourRef.current = true;

    const checkTourStatus = async () => {
      try {
        const res = await fastapi.get('/auth/me');
        if (
          res.data &&
          res.data.onboarding_tour_enabled === true &&
          res.data.onboarding_tour_completed === false
        ) {
          setIsTourModalOpen(true);
        }
      } catch (e) {
        console.warn('Could not check onboarding tour status:', e);
      }
    };
    checkTourStatus();
  }, [user]);

  const sidebarWidth = isCollapsed ? 72 : 240;
  const chatWidth = isDesktop && isChatOpen && isChatDocked ? 900 : 0;

  if (user && !demoChecked) {
    return <div className="min-h-screen bg-transparent" aria-label="Cargando comercio" />;
  }

  return (
    <div data-ui="app-layout" data-theme={theme} className="min-h-screen bg-transparent font-sans">
      {/* Left sidebar (fixed) */}
      <Sidebar />

      {/* Main content area, padded for desktop sidebar and docked right chat */}
      <main
        data-ui="app-main-content"
        className="w-full pt-16 lg:pt-2 pb-2 px-2 sm:px-4 transition-all duration-300 min-h-screen min-w-0"
        style={{
          paddingLeft: isDesktop ? sidebarWidth + 12 : 8,
          paddingRight: isDesktop && isChatOpen && isChatDocked ? chatWidth + 24 : 8,
        }}
      >
        <MetaLimitNotice key={String(user?.comercioKey ?? "operator")} />
        {children}
      </main>

      {/* Chat Sidebar with floating button */}
      <ChatSidebar />

      {/* Guided Onboarding Tour Spotlight Modal */}
      <OnboardingTourModal
        isOpen={isTourModalOpen}
        onClose={() => setIsTourModalOpen(false)}
      />
    </div>
  );
};

function Layout({ children }: LayoutProps) {
  return <LayoutContent>{children}</LayoutContent>;
}

export default Layout;
